import { ProcessImage } from '../memory/ProcessImage';
import { Interpreter } from './Interpreter';
import type { ProgramIR } from './ir';
import type { SchedulerTask } from '../../core/clock/Scheduler';
import { TimerManager } from '../core/TimerManager';
import { CounterManager } from '../core/CounterManager';

export type RuntimeState = 'STOPPED' | 'RUNNING' | 'PAUSED';

export interface RuntimeConfig {
  readonly scanTimeMs: number;
}

export class RuntimeHost implements SchedulerTask {
  public readonly name = 'plc_runtime';
  public readonly intervalMs: number;

  public state: RuntimeState = 'STOPPED';
  public readonly pi: ProcessImage;
  public readonly timers: TimerManager;
  public readonly counters: CounterManager;
  private readonly interpreter: Interpreter;
  private program: ProgramIR = [];

  // Mocked physical buffers. In a real scenario, an IoProvider syncs these with the simulation.
  public readonly physicalInputs: Uint8Array;
  public readonly physicalOutputs: Uint8Array;

  constructor(config: RuntimeConfig = { scanTimeMs: 10 }) {
    this.intervalMs = config.scanTimeMs;
    this.pi = new ProcessImage(128, 128, 1024);
    this.timers = new TimerManager();
    this.counters = new CounterManager();
    this.interpreter = new Interpreter(this.pi, this.timers, this.counters);
    this.physicalInputs = new Uint8Array(128);
    this.physicalOutputs = new Uint8Array(128);
  }

  loadProgram(program: ProgramIR): void {
    this.program = program;
  }

  start(): void {
    this.state = 'RUNNING';
  }

  stop(): void {
    this.state = 'STOPPED';
    this.pi.reset();
    this.timers.reset();
    this.counters.resetAll();
    this.interpreter.reset();
    this.physicalInputs.fill(0);
    this.physicalOutputs.fill(0);
  }

  // The Scheduler calls this deterministically based on intervalMs
  execute(_simTimeMs: number, dtMs: number): void {
    if (this.state !== 'RUNNING') return;
    this.runSingleScan(dtMs);
  }

  private runSingleScan(dtMs: number): void {
    // 1. Read Inputs
    this.pi.readInputs(this.physicalInputs);

    // 2. Execute Logic
    this.interpreter.execute(this.program);

    // 3. Update Timers (use elapsed simulation delta)
    this.timers.tick(dtMs);

    // 4. Write Outputs
    this.pi.writeOutputs(this.physicalOutputs);
  }
}

